<script setup lang="ts">
import { reactive, ref } from "vue"
import { Link, router } from "@inertiajs/vue3"
import { CalendarDaysIcon, EyeIcon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable, UiDrawer, UiTimeline } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"
import { formatDate, formatDateTime } from "@/utils/date"

type SalesOrderRow = {
  id: number
  internal_reference_code: string
  company: string
  customer_name: string
  status: string
  raw_status: string
  health_status: string
  raw_health_status: string
  delivery_date: string | null
  projected_revenue_net: string
  projected_total_cost: string
  projected_operating_profit: string
  projected_margin_percent: string
  actual_revenue_net: string
  actual_total_cost: string
  actual_operating_profit: string
  actual_margin_percent: string
  items_count: number
  linked_production_order_id: number | null
  can_export_pdf: boolean
  can_approve: boolean
  can_start: boolean
  can_close: boolean
  can_cancel: boolean
}

type SalesOrderDetail = {
  id: number
  internal_reference_code: string
  company: string
  customer_name: string
  status: string
  raw_status: string
  health_status: string
  raw_health_status: string
  delivery_date: string | null
  created_at: string | null
  approved_at: string | null
  started_at: string | null
  closed_at: string | null
  linked_production_order: {
    id: number
    status: string
    status_label: string
    done_at: string | null
    href: string
  } | null
  created_by: string
  approved_by: string
  started_by: string
  closed_by: string

  projected: {
    revenue_gross: string
    revenue_net: string
    tax_amount: string
    freight_amount: string
    packaging_amount: string
    overhead_amount: string
    indirect_costs_amount?: string
    material_cost: string
    total_cost: string
    operating_profit: string
    contribution_margin: string
    margin_percent: string
    cost_breakdown?: Array<{
      key: string
      label: string
      amount: string
      nature: string
      notes: string
    }>
  }
  actual: {
    revenue_gross: string
    revenue_net: string
    tax_amount: string
    freight_amount: string
    packaging_amount: string
    overhead_amount: string
    indirect_costs_amount?: string
    material_cost: string
    total_cost: string
    operating_profit: string
    contribution_margin: string
    margin_percent: string
    cost_breakdown?: Array<{
      key: string
      label: string
      amount: string
      nature: string
      notes: string
    }>
  }
  items: Array<{
    product: string
    quantity: string
    unit: string
    unit_price: string
    line_revenue: string
    projected_material_cost: string
    actual_material_cost: string
  }>
  can_export_pdf: boolean
  can_approve: boolean
  can_start: boolean
  can_close: boolean
  can_cancel: boolean
}

type TimelineStep = {
  id: string
  title: string
  description: string
  icon: "create" | "send" | "receive" | "factory" | "done" | "cancel" | "clock"
  state: "done" | "pending" | "warning" | "danger"
}

const props = defineProps<{
  sales_orders: SalesOrderRow[]
  companies: Array<{ id: number; name: string }>
  statuses: Array<{ label: string; value: string }>
  query: {
    q?: string
    company_id?: string
    status?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "internal_reference_code", label: "Referencia" },
  { key: "company", label: "Empresa" },
  { key: "customer_name", label: "Cliente" },
  { key: "status", label: "Status", align: "center" },
  { key: "health_status", label: "Saude", align: "center" },
  { key: "projected_operating_profit", label: "Lucro proj.", align: "right" },
  { key: "projected_margin_percent", label: "Margem proj.", align: "right" },
  { key: "delivery_date", label: "Entrega", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
  {
    key: "status",
    label: "Status",
    options: props.statuses,
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
  status: props.query.status ?? "",
})

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
})

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const statusBadgeClassByStatus: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  approved: "bg-sky-50 text-sky-700 border-sky-200",
  in_production: "bg-amber-50 text-amber-700 border-amber-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
}

const healthBadgeClassByStatus: Record<string, string> = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  attention: "bg-amber-50 text-amber-700 border-amber-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
}

const isDetailsDrawerOpen = ref(false)
const isLoadingDetail = ref(false)
const selectedDetail = ref<SalesOrderDetail | null>(null)
const selectedOrderId = ref<number | null>(null)

const { confirm } = useConfirmation()
const notify = useNotifications()

function statusBadgeClass(status: string): string {
  return statusBadgeClassByStatus[status] ?? statusBadgeClassByStatus.draft
}

function healthBadgeClass(status: string): string {
  return healthBadgeClassByStatus[status] ?? healthBadgeClassByStatus.pending
}

function formatCurrency(raw: string | number): string {
  return currencyFormatter.format(Number(raw || 0))
}

function formatPercent(raw: string | number): string {
  return `${percentFormatter.format(Number(raw || 0))}%`
}

function toNumber(raw: string | number): number {
  return Number(raw || 0)
}

const healthBadgeMeaningByStatus: Record<string, string> = {
  healthy: "Saudavel: margem projetada confortavel e custos sob controle.",
  attention: "Atencao: margem apertada; monitore desvios de custo/frete/impostos.",
  critical: "Critico: resultado projetado negativo ou risco alto de prejuizo.",
  pending: "Pendente: aguardando dados suficientes para classificacao definitiva.",
}

function healthBadgeMeaning(status: string): string {
  return healthBadgeMeaningByStatus[status] || healthBadgeMeaningByStatus.pending
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    status: String(overrides.status ?? selectedFilters.status ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/sales_orders", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

function salesTimelineSteps(detail: SalesOrderDetail): TimelineStep[] {
  const hasStartedProduction = Boolean(detail.started_at)
  const productionDone = Boolean(detail.linked_production_order?.done_at)

  const waitingProductionState: TimelineStep["state"] = hasStartedProduction && !productionDone
    ? "warning"
    : (productionDone ? "done" : "pending")

  const waitingProductionDescription = !hasStartedProduction
    ? "Aguardando envio para producao"
    : (productionDone ? "Etapa concluida" : "Aguardando conclusao da producao")

  return [
    {
      id: "created",
      title: "Pedido criado",
      description: `${formatDateTime(detail.created_at)} por ${detail.created_by || "Nao informado"}`,
      icon: "create",
      state: "done",
    },
    {
      id: "approved",
      title: "Pedido aprovado",
      description: detail.approved_at
        ? `${formatDateTime(detail.approved_at)} por ${detail.approved_by || "Nao informado"}`
        : "Pendente",
      icon: "done",
      state: detail.approved_at ? "done" : "pending",
    },
    {
      id: "started",
      title: "Enviado para producao",
      description: detail.started_at
        ? `${formatDateTime(detail.started_at)} por ${detail.started_by || "Nao informado"}`
        : "Pendente",
      icon: "factory",
      state: detail.started_at ? "done" : "pending",
    },
    {
      id: "waiting-production",
      title: "Aguardando producao",
      description: waitingProductionDescription,
      icon: "clock",
      state: waitingProductionState,
    },
    {
      id: "production-done",
      title: "Producao concluida",
      description: detail.linked_production_order?.done_at
        ? formatDateTime(detail.linked_production_order.done_at)
        : "Pendente",
      icon: "receive",
      state: detail.linked_production_order?.done_at ? "done" : "pending",
    },
    {
      id: "closed",
      title: "Pedido concluido",
      description: detail.closed_at
        ? `${formatDateTime(detail.closed_at)} por ${detail.closed_by || "Nao informado"}`
        : "Pendente",
      icon: "done",
      state: detail.closed_at ? "done" : "pending",
    },
  ]
}

async function openDetailsDrawer(orderId: number) {
  selectedOrderId.value = orderId
  selectedDetail.value = null
  isLoadingDetail.value = true
  isDetailsDrawerOpen.value = true

  try {
    const response = await fetch(`/sales_orders/${orderId}/detail`, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
      },
    })

    if (response.ok) {
      selectedDetail.value = await response.json()
    }
  } finally {
    isLoadingDetail.value = false
  }
}

function runTransition(orderId: number, action: "approve" | "start" | "close" | "cancel", successMessage: string) {
  router.patch(`/sales_orders/${orderId}/${action}`, {}, {
    preserveScroll: true,
    onSuccess: () => {
      notify.success(successMessage)
      if (isDetailsDrawerOpen.value && selectedOrderId.value === orderId) {
        openDetailsDrawer(orderId)
      }
      fetchWithQuery()
    },
  })
}

async function confirmAndRunTransition(params: {
  orderId: number
  action: "approve" | "start" | "close" | "cancel"
  title: string
  message: string
  confirmText: string
  successMessage: string
  type?: "danger" | "warning" | "info"
}) {
  const ok = await confirm({
    title: params.title,
    message: params.message,
    type: params.type ?? "warning",
    confirmText: params.confirmText,
  })
  if (!ok) return

  runTransition(params.orderId, params.action, params.successMessage)
}

function approveOrder(orderId: number) {
  confirmAndRunTransition({
    orderId,
    action: "approve",
    title: "Aprovar pedido",
    message: "Deseja aprovar este pedido de venda? O snapshot financeiro sera congelado.",
    confirmText: "Aprovar",
    successMessage: "Pedido aprovado",
  })
}

function startOrder(orderId: number) {
  confirmAndRunTransition({
    orderId,
    action: "start",
    title: "Enviar para producao",
    message: "Deseja iniciar a producao deste pedido?",
    confirmText: "Enviar para producao",
    successMessage: "Pedido enviado para producao",
    type: "info",
  })
}

function closeOrder(orderId: number) {
  confirmAndRunTransition({
    orderId,
    action: "close",
    title: "Concluir pedido",
    message: "Deseja concluir este pedido de venda?",
    confirmText: "Concluir pedido",
    successMessage: "Pedido concluido",
    type: "info",
  })
}

function cancelOrder(orderId: number) {
  confirmAndRunTransition({
    orderId,
    action: "cancel",
    title: "Cancelar pedido",
    message: "Deseja cancelar este pedido de venda?",
    confirmText: "Cancelar pedido",
    successMessage: "Pedido cancelado",
    type: "danger",
  })
}

function openOrderPdf(orderId: number) {
  window.open(`/sales_orders/${orderId}/export_pdf`, "_blank", "noopener,noreferrer")
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Pedidos de venda</h1>
        <p class="mt-2 text-sm text-slate-600">Acompanhe status comercial e rentabilidade projetada por pedido.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="sales_orders"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/sales_orders/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo pedido
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Visualizar pedido"
              @click="openDetailsDrawer((row as SalesOrderRow).id)"
            >
              <EyeIcon class="size-4" />
            </button>

            <UiButton v-if="(row as SalesOrderRow).can_export_pdf" size="sm" variant="secondary" @click="openOrderPdf((row as SalesOrderRow).id)">
              PDF
            </UiButton>

            <UiButton v-if="(row as SalesOrderRow).can_approve" size="sm" variant="secondary" @click="approveOrder((row as SalesOrderRow).id)">
              Aprovar
            </UiButton>

            <UiButton v-if="(row as SalesOrderRow).can_start" size="sm" variant="primary" @click="startOrder((row as SalesOrderRow).id)">
              Produzir
            </UiButton>

            <UiButton v-if="(row as SalesOrderRow).can_close" size="sm" variant="primary" @click="closeOrder((row as SalesOrderRow).id)">
              Concluir
            </UiButton>

            <UiButton v-if="(row as SalesOrderRow).can_cancel" size="sm" variant="danger" @click="cancelOrder((row as SalesOrderRow).id)">
              Cancelar
            </UiButton>
          </div>

          <span
            v-else-if="column.key === 'status'"
            class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold"
            :class="statusBadgeClass((row as SalesOrderRow).raw_status)"
          >
            {{ (row as SalesOrderRow).status }}
          </span>

          <span
            v-else-if="column.key === 'health_status'"
            class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold"
            :class="healthBadgeClass((row as SalesOrderRow).raw_health_status)"
          >
            {{ (row as SalesOrderRow).health_status }}
          </span>

          <span v-else-if="column.key === 'projected_operating_profit'">{{ formatCurrency((row as SalesOrderRow).projected_operating_profit) }}</span>
          <span v-else-if="column.key === 'projected_margin_percent'">{{ formatPercent((row as SalesOrderRow).projected_margin_percent) }}</span>
          <span v-else-if="column.key === 'delivery_date'">{{ formatDate((row as SalesOrderRow).delivery_date) }}</span>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>

      <UiDrawer v-model="isDetailsDrawerOpen" title="Detalhes do pedido de venda" size="2xl">
        <p v-if="isLoadingDetail" class="py-4 text-center text-sm text-slate-400">Carregando detalhes...</p>

        <div v-else-if="selectedDetail" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-xs uppercase tracking-wide text-slate-500">Pedido de venda</p>
                <h3 class="text-lg font-semibold text-slate-900">{{ selectedDetail.internal_reference_code }} - {{ selectedDetail.customer_name }}</h3>
                <p class="mt-1 text-sm text-slate-600">Empresa: {{ selectedDetail.company }}</p>
              </div>

              <div class="flex gap-2">
                <span class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold" :class="statusBadgeClass(selectedDetail.raw_status)">
                  {{ selectedDetail.status }}
                </span>
                <span class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold" :class="healthBadgeClass(selectedDetail.raw_health_status)">
                  {{ selectedDetail.health_status }}
                </span>
              </div>
            </div>

            <div class="mt-3 flex flex-wrap justify-end gap-2">
              <UiButton v-if="selectedDetail.can_export_pdf" size="sm" variant="secondary" @click="openOrderPdf(selectedDetail.id)">Gerar PDF</UiButton>
              <UiButton v-if="selectedDetail.can_approve" size="sm" variant="secondary" @click="approveOrder(selectedDetail.id)">Aprovar</UiButton>
              <UiButton v-if="selectedDetail.can_start" size="sm" variant="primary" @click="startOrder(selectedDetail.id)">Enviar para producao</UiButton>
              <UiButton v-if="selectedDetail.can_close" size="sm" variant="primary" @click="closeOrder(selectedDetail.id)">Concluir pedido</UiButton>
              <UiButton v-if="selectedDetail.can_cancel" size="sm" variant="danger" @click="cancelOrder(selectedDetail.id)">Cancelar pedido</UiButton>
            </div>

            <p class="mt-2 text-xs text-slate-600">
              Classificacao atual: <span class="font-semibold">{{ selectedDetail.health_status }}</span> -
              {{ healthBadgeMeaning(selectedDetail.raw_health_status) }}
            </p>

            <div v-if="selectedDetail.linked_production_order" class="mt-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
              <span class="font-medium">OP vinculada:</span>
              #{{ selectedDetail.linked_production_order.id }} - {{ selectedDetail.linked_production_order.status_label }}
              <Link :href="selectedDetail.linked_production_order.href" class="ml-2 font-medium underline underline-offset-2">
                Abrir ordem de producao
              </Link>
            </div>

            <div class="mt-4 grid gap-3 md:grid-cols-3">
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Entrega prevista</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ formatDate(selectedDetail.delivery_date) }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Lucro projetado</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ formatCurrency(selectedDetail.projected.operating_profit) }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Margem projetada</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ formatPercent(selectedDetail.projected.margin_percent) }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p class="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <CalendarDaysIcon class="size-4 text-slate-500" /> Timeline do pedido
            </p>

            <UiTimeline :steps="salesTimelineSteps(selectedDetail)" />
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p class="mb-3 text-sm font-semibold text-slate-800">Demonstrativo detalhado</p>

            <div class="grid gap-3 md:grid-cols-2">
              <section class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <h5 class="text-xs font-semibold uppercase tracking-wide text-slate-600">Projetado</h5>
                <dl class="mt-2 grid gap-2 text-sm">
                  <div class="flex items-center justify-between"><dt class="text-slate-500">Receita bruta</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.projected.revenue_gross) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Impostos</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.projected.tax_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">= Receita liquida</dt><dd class="font-semibold text-slate-900">{{ formatCurrency(selectedDetail.projected.revenue_net) }}</dd></div>

                  <div class="my-1 border-t border-slate-200" aria-hidden="true" />

                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Materia-prima</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.projected.material_cost) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Embalagem</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.projected.packaging_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Frete</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.projected.freight_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">= Margem de contribuicao</dt><dd class="font-semibold text-slate-900">{{ formatCurrency(selectedDetail.projected.contribution_margin) }}</dd></div>

                  <div class="my-1 border-t border-slate-200" aria-hidden="true" />

                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Custos indiretos</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.projected.indirect_costs_amount || selectedDetail.projected.overhead_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">= Lucro operacional</dt><dd class="font-semibold text-slate-900">{{ formatCurrency(selectedDetail.projected.operating_profit) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">Margem (%)</dt><dd class="font-semibold text-slate-900">{{ formatPercent(selectedDetail.projected.margin_percent) }}</dd></div>

                  <div class="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-500">
                    Formula: (Lucro operacional / Receita liquida) x 100 =
                    {{ formatPercent((toNumber(selectedDetail.projected.operating_profit) / Math.max(toNumber(selectedDetail.projected.revenue_net), 1e-9)) * 100) }}
                  </div>
                </dl>

                <div v-if="selectedDetail.projected.cost_breakdown?.length" class="mt-3 rounded border border-slate-200 bg-white p-2">
                  <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Composicao de custos</p>
                  <ul class="space-y-1 text-xs text-slate-600">
                    <li v-for="component in selectedDetail.projected.cost_breakdown" :key="`projected-${component.key}`" class="flex items-start justify-between gap-3">
                      <span>
                        {{ component.label }}
                        <span v-if="component.notes" class="text-slate-500">- {{ component.notes }}</span>
                      </span>
                      <span class="font-medium text-slate-900">{{ formatCurrency(component.amount) }}</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <h5 class="text-xs font-semibold uppercase tracking-wide text-slate-600">Realizado</h5>
                <dl class="mt-2 grid gap-2 text-sm">
                  <div class="flex items-center justify-between"><dt class="text-slate-500">Receita bruta</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.actual.revenue_gross) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Impostos</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.actual.tax_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">= Receita liquida</dt><dd class="font-semibold text-slate-900">{{ formatCurrency(selectedDetail.actual.revenue_net) }}</dd></div>

                  <div class="my-1 border-t border-slate-200" aria-hidden="true" />

                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Materia-prima</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.actual.material_cost) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Embalagem</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.actual.packaging_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Frete</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.actual.freight_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">= Margem de contribuicao</dt><dd class="font-semibold text-slate-900">{{ formatCurrency(selectedDetail.actual.contribution_margin) }}</dd></div>

                  <div class="my-1 border-t border-slate-200" aria-hidden="true" />

                  <div class="flex items-center justify-between"><dt class="text-slate-500">(-) Custos indiretos</dt><dd class="font-medium text-slate-900">{{ formatCurrency(selectedDetail.actual.indirect_costs_amount || selectedDetail.actual.overhead_amount) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">= Lucro operacional</dt><dd class="font-semibold text-slate-900">{{ formatCurrency(selectedDetail.actual.operating_profit) }}</dd></div>
                  <div class="flex items-center justify-between"><dt class="text-slate-500">Margem (%)</dt><dd class="font-semibold text-slate-900">{{ formatPercent(selectedDetail.actual.margin_percent) }}</dd></div>
                </dl>

                <div v-if="selectedDetail.actual.cost_breakdown?.length" class="mt-3 rounded border border-slate-200 bg-white p-2">
                  <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Composicao de custos</p>
                  <ul class="space-y-1 text-xs text-slate-600">
                    <li v-for="component in selectedDetail.actual.cost_breakdown" :key="`actual-${component.key}`" class="flex items-start justify-between gap-3">
                      <span>
                        {{ component.label }}
                        <span v-if="component.notes" class="text-slate-500">- {{ component.notes }}</span>
                      </span>
                      <span class="font-medium text-slate-900">{{ formatCurrency(component.amount) }}</span>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Itens</h4>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th class="px-3 py-2">Produto</th>
                    <th class="px-3 py-2 text-right">Qtd</th>
                    <th class="px-3 py-2 text-right">Preco unit.</th>
                    <th class="px-3 py-2 text-right">Receita linha</th>
                    <th class="px-3 py-2 text-right">Custo mat. proj.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in selectedDetail.items" :key="item.product" class="border-b border-slate-100">
                    <td class="px-3 py-2 text-slate-700">{{ item.product }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ item.quantity }} {{ item.unit }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ formatCurrency(item.unit_price) }}</td>
                    <td class="px-3 py-2 text-right font-medium text-slate-900">{{ formatCurrency(item.line_revenue) }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ formatCurrency(item.projected_material_cost) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </UiDrawer>
    </div>
  </AppShell>
</template>


